import React from 'react';
import { Link } from 'react-router-dom';
import '../../CSS/Blog.css';
import '../../CSS/App.css';

const AICloneBlog = () => {
  return (
    <div className="blog-container">
      <Link to="/blog" className="blog-back-link">← Back to Blog</Link>

      <p className="blog-post-title">Personalized Conversational Agent Trained on Instagram Messages</p>
      <p className="blog-meta">LLM Fine-tuning · Python · Unsloth · LoRA · Senior Capstone</p>

      <div className="blog-body">
        <p>
          For my senior capstone, I decided to try something I had been thinking about for a while. I wanted
          to see if I could take an LLM and fine-tune it on my own messages enough that it could actually text
          like me. Not just give similar answers to what I would say, but actually copy the way I text, like
          how long my messages are, the words I use, and how I sometimes send multiple short messages instead
          of one big paragraph.
        </p>
        <p>
          The idea came from me wondering how much of someone's "voice" you can actually learn just from their
          messages. Most LLMs are made to sound helpful and pretty neutral, which makes sense for what they're
          used for, but I wanted basically the opposite of that. I wanted to take a model and force it toward
          one specific person as much as I could. Using my own Instagram DMs made the most sense because I
          already know what I sound like, so I could actually tell when the model was getting closer to me and
          when it wasn't.
        </p>

        <h2>Getting It to Actually Train</h2>
        <p>
          Originally I tried doing everything on my MacBook Air M2. It was fine for processing all of the
          messages, but actually training the model was another story. I didn't have a dedicated GPU, so every
          training step took forever and my MacBook would get hot and slow down.
        </p>
        <p>
          Eventually I borrowed my brother's desktop with an RTX 3060 and the difference was huge. Training
          went from taking multiple hours for a single epoch to a little under an hour. I was also able to use
          4-bit training, which made running everything on normal consumer hardware actually practical. I
          didn't really want to pay for cloud GPUs for a school project either, so being able to do the entire
          thing locally was nice.
        </p>

        <h2>How It Works</h2>
        <p>
          I split the project into a few different parts instead of having one giant script do everything.
        </p>
        <p>
          The parser takes the JSON files Instagram gives you and turns them into something actually useful.
          It fixes broken emoji encoding, removes things like reactions and call notifications, gets rid of
          group chats, and builds conversations with up to 15 previous messages for context.
        </p>
        <p>
          After that, the cleaner gets rid of stuff I don't really want the model learning from, like
          standalone links and other non-text content. The DatasetBuilder then turns everything into JSONL
          that can actually be used for training.
        </p>
        <p>
          The Trainer handles the actual fine-tuning with Unsloth. I made it save a checkpoint every 200 steps
          because I really didn't want to lose hours of training if something crashed. It can also find the
          newest checkpoint and continue from there.
        </p>
        <p>
          Then there's the InferenceEngine, which loads one of those checkpoints and generates messages using
          the recent conversation as context. I also made a ChatController because one weird problem I ran
          into was that even when the model learned what I would say, it didn't necessarily learn <em>how</em> I
          text. I tend to send thoughts as a few smaller messages instead of one giant message, so the
          controller splits the response into separate bubbles to make it feel more like an actual
          conversation with me.
        </p>

        <h2>Training and Figuring Out When to Stop</h2>
        <p>
          One thing I learned pretty quickly is that more training doesn't automatically mean a better model.
          I compared checkpoints from around 1200 to 2000 steps and found that the ones in that range usually
          sounded the most natural.
        </p>
        <p>
          If I kept training it past that point, it actually started getting worse. It would overfit the
          messages and feel more like it was copying the dataset rather than actually talking like me. So a
          big part of the project became figuring out when the model had learned enough.
        </p>
        <p>
          I used Unsloth for the LoRA fine-tuning, Hugging Face Transformers for loading the model and
          generating text, and PyTorch for the actual model operations.
        </p>

        <h2>Result</h2>
        <p>
          In the end I got a completely local program that can have conversations while imitating the way I
          text. Obviously it's not literally me and sometimes it says something I never would, but a lot of
          the responses are surprisingly convincing.
        </p>
        <p>
          The funny part is that the actual LLM wasn't really the hardest part. Most of my time went into the
          data. I had to figure out what messages were actually useful, what should be removed, how much
          previous conversation the model should see, and how to represent the fact that I might send three
          messages in a row as one response.
        </p>
        <p>
          I originally saw this mostly as an AI project, but by the end it felt like a mix of machine
          learning, data processing, and regular software engineering. It also made me realize that getting
          good data and structuring it correctly can matter just as much as the model itself.
        </p>
      </div>
    </div>
  );
};

export default AICloneBlog;
